import React from 'react';
import ChatMessage from './ChatMessage';
import DateSeparator from './DateSeparator';
import SystemMessage from './SystemMessage';

const ChatMessages = React.memo(({
  messages,
  isLoading,
  transformMessageForUI,
  groupMessagesByDate,
  handleScroll,
  messagesEndRef
}) => {
  try {
    // Ensure messages is an array
    const messagesArray = Array.isArray(messages) ? messages : [];

    // Transform API messages to UI format with error handling
    const transformedMessages = messagesArray.map((message, index) => {
      try {
        return transformMessageForUI(message);
      } catch (error) {
        console.error('Error transforming message:', message, error);
        // Return a fallback message object with unique ID
        return {
          id: message?.id || `error-${Date.now()}-${index}`,
          text: 'Message could not be displayed',
          timestamp: new Date().toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }),
          fullTimestamp: new Date(),
          isOutgoing: false,
          agentName: null,
          messageType: 'error',
          chatMessageType: 'inbound', // Default to inbound for error messages
          status: 'error',
          messageId: message?.message_id || null
        };
      }
    });

    // Sort messages by timestamp to ensure proper chronological order
    // This is especially important when SSE messages are added
    const sortedMessages = transformedMessages.sort((a, b) => {
      return a.fullTimestamp.getTime() - b.fullTimestamp.getTime();
    });

    // Remove any potential duplicates based on message ID
    const uniqueMessages = sortedMessages.filter((message, index, array) => {
      return array.findIndex(m => m.id === message.id) === index;
    });

    // Group messages by date
    const messageGroups = groupMessagesByDate(uniqueMessages);

    return (
      <div
        style={{
          flex: '1',
          padding: '20px',
          overflowY: 'auto',
          backgroundColor: '#FFFFFF',
          height: '100%', // Use full available height
          maxHeight: '100%', // Prevent exceeding container
          minHeight: '200px' // Minimum height for content
        }}
        onScroll={handleScroll}
      >
        {isLoading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: '200px',
            color: '#667085',
            fontFamily: 'Poppins',
            fontSize: '14px'
          }}>
            Loading messages...
          </div>
        ) : messageGroups.length === 0 ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: '200px',
            color: '#667085',
            fontFamily: 'Poppins',
            fontSize: '14px'
          }}>
            No messages yet
          </div>
        ) : (
          <>
            {messageGroups.map((group, groupIndex) => (
              <React.Fragment key={group.date}>
                <DateSeparator date={group.date} />
                {group.messages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    messageType={message.chatMessageType}
                  />
                ))}
                {/* Add system message for conversation start if this is the first group */}
                {groupIndex === 0 && (
                  <SystemMessage message="Conversation started" />
                )}
              </React.Fragment>
            ))}
            {/* Invisible element to scroll to */}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
    );
  } catch (error) {
    console.error('Error rendering chat messages:', error);
    return (
      <div style={{
        flex: '1',
        padding: '20px',
        overflowY: 'auto',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '200px'
      }}>
        <div style={{
          textAlign: 'center',
          color: '#667085',
          fontFamily: 'Poppins',
          fontSize: '14px'
        }}>
          <p>Unable to load messages</p>
          <p style={{ fontSize: '12px', marginTop: '8px' }}>
            Please try refreshing the page
          </p>
        </div>
      </div>
    );
  }
});

ChatMessages.displayName = 'ChatMessages';

export default ChatMessages;
